import Backbone from 'backbone';
import _ from 'underscore';
import ko from 'knockout';

import { APPLY_USERINPUT_TYPES, isPlural } from '@bingads-webui-campaign/recommendation-core';

import ViewModel from '../viewmodel/index';
import template from '../template/index.pug';

export class RecommendationFixAdDestinationView extends Backbone.View {
  initialize({
    data,
    i18n,
    deps,
    router,
    gridView,
    readOnly,
    UrlTestTool,
  }) {
    this.opportunityData = _.first(data.Opportunities);
    this.type = data.OpportunityType;
    this.i18n = i18n;
    this.data = data;
    this.deps = deps;
    this.router = router;
    this.grid = { gridView };

    this.rejectedAd = _.first(this.opportunityData.RejectedAds) ||
      _.first(this.opportunityData.RejectedKeywords) || {};

    this.viewModel = new ViewModel({
      i18n,
      view: this,
      data: this.opportunityData,
      type: this.type,
      deps,
      readOnly,
      rejectedAd: this.rejectedAd,
    });

    this.info = {
      landingPageUrl: this.opportunityData.UrlOrLandingPage,
      finalUrl: this.rejectedAd.FinalUrl,
      finalUrlDesc: i18n.getString(
        `Recommendation_FixAdDestinationInline_FinalUrl_Desc_${isPlural(this.opportunityData.NumberRejectedAds)}_${isPlural(this.opportunityData.NumberRejectedKeywords)}`,
        {
          ad: this.opportunityData.NumberRejectedAds,
          keyword: this.opportunityData.NumberRejectedKeywords,
        }
      ),
      desc: i18n.getString('Recommendation_FixAdDestinationInline_Desc', {
        type: i18n.getString(`Details_View_Rejection_Type_${this.opportunityData.FlagId}`),
      }),
      numberRejectedAds: this.opportunityData.NumberRejectedAds,
      numberRejectedKeywords: this.opportunityData.NumberRejectedKeywords,
      showTrackingTemplate: !_.isEmpty(this.rejectedAd.TrackingTemplate),
    };

    this.finalUrlEditor = new deps.UrlEditor({
      url: this.rejectedAd.FinalUrl,
      ariaLabelledby: 'finalUrlLabel',
      ariaId: 'FinalUrl',
    });

    this.finalUrlEditor.on('urlChanged', (url) => {
      this.viewModel.finalUrl(url);
    });

    this.viewModel.finalUrl.subscribe((value) => {
      const hasError = _.isEmpty(value);
      this.viewModel.enableApply(!hasError);
      this.finalUrlEditor.hasErrors(hasError);
      this.finalUrlEditor.setError(hasError ? this.i18n.getString('Validation_NotEmpty_Required_Field') : '');
    });

    this.trackingTemplate = new deps.TrackingTemplate({
      placeholderEnabled: true,
      templateExampleEnabled: false,
    });
    this.trackingTemplate.setTrackingTemplate(this.rejectedAd.TrackingTemplate);

    this.trackingTemplate.on('templateChanged', (trackingTemplate) => {
      this.viewModel.showWarning(false);
      this.viewModel.showEmptyTemplateWarning(false);
      if (_.isEmpty(trackingTemplate)) {
        this.viewModel.showEmptyTemplateWarning(true);
      } else {
        const showWarning = this.trackingTemplate.isValid()
          && trackingTemplate !== this.rejectedAd.TrackingTemplate;
        this.viewModel.showWarning(showWarning);
      }
    });

    this.urlTestTool = new UrlTestTool({});

    this.urlTestTool.on('testUurl', (fn, originalView) => {
      const view = originalView;
      const { pageContext, odata } = this.deps;
      const target = this.opportunityData.Target;

      view.isInline = false;
      view.level = this.deps.uurlLevels.Ad;

      const url = `/Customers(${pageContext.CurrentCustomer.Id})/Accounts(${target.AccountId})/Ads/Default.GridData?$filter=Id+eq+${this.rejectedAd.AdId}&$top=200`;

      const timeFormat = { raw: 'yyyy-MM-dd', timeZone: 'UTC' };
      const startDate = i18n.formatDate(new Date(null), timeFormat);
      const endDate = i18n.formatDate(new Date(), timeFormat);

      odata.post(
        url,
        {
          data: {
            gridDataParameters: {
              DateRange: {
                EndDate: endDate,
                StartDate: startDate,
              },
            },
          },
        }
      ).then((res) => {
        const adsInfo = res.value[0] || {};
        const params = {
          FinalUrls: [this.viewModel.finalUrl()],
          TrackingUrlTemplate: this.trackingTemplate.getTrackingTemplate(),
          UrlCustomParameters: adsInfo.UrlCustomParameters || [],
          FinalUrlSuffix: adsInfo.FinalUrlSuffix,
          AccountFinalUrlSuffix: null,
        };

        const context = {
          customerId: pageContext.CurrentCustomer.Id,
          userId: pageContext.CurrentUser.Id,
          accountId: target.AccountId,
          campaignId: target.CampaignId || '',
          adGroupId: target.AdGroupId || '',
          entityId: this.rejectedAd.AdId,
        };
        const options = {
          apiInLog: 'adEditor/uurlTest',
        };
        this.deps.testUurl(fn, view, params, context, options);
      });
    });
  }

  get events() {
    return {
      'click .apply': this.apply,
      'click .btn-cancel': this.cancel,
    };
  }

  render() {
    const {
      i18n,
      type,
      opportunityData,
      info,
    } = this;

    const model = _.extend({
      i18n,
      type,
      opportunityData,
      info,
    });
    this.$el.html(template(model));

    ko.applyBindings(this.viewModel, this.el);

    this.$('.final-url').empty().append(this.finalUrlEditor.render().el);

    this.$('.tracking-template').empty().append(this.trackingTemplate.render().el);

    if (this.opportunityData.NumberRejectedAds > 0) {
      this.$('.url-test-tool').empty().append(this.urlTestTool.render().el);
    }

    this.$('.tipsCarousel').on('slid.bs.carousel', () => {
      const index = this.$('.tipsCarousel .ad-preview.active').index();
      this.viewModel.selectIndex(index);
    });

    return this;
  }

  isValid() {
    return this.trackingTemplate.isValid() && this.viewModel.enableApply();
  }

  apply() {
    if (!this.isValid()) {
      return;
    }

    const {
      type,
      opportunityData,
    } = this;

    const odataType = APPLY_USERINPUT_TYPES[type];
    const { OpportunityId } = opportunityData;
    const fields = ['FinalUrl', 'TrackingTemplate'];
    const model = this.rejectedAd;

    const userInputs = {
      '@odata.type': odataType,
      OpportunityId,
      FinalUrl: this.viewModel.finalUrl(),
      TrackingTemplate: this.trackingTemplate.getTrackingTemplate(),
    };

    _.map(fields, (item) => {
      if (
        model[item] === userInputs[item] ||
        (_.isEmpty(model[item]) && _.isEmpty(userInputs[item]))
      ) {
        userInputs[item] = null;
      }
    });

    this.trigger('apply', {
      opportunities: [this.opportunityData],
      userInputs: [userInputs],
    });
  }

  cancel() {
    this.trigger('cancel');
  }
}
